<!-- Notifications toggle -->
<button
  mat-icon-button
  (click)="openPanel()"
  #notificationsOrigin
  [class.animate-pulse]="hasNewNotifications()"
>
  @if (unreadCount() > 0) {
    <span
      class="absolute left-0 right-0 top-0 flex h-3 items-center justify-center"
    >
      <span
        class="ml-4 mt-2.5 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-teal-600 px-1 text-xs font-medium text-indigo-50 z-10"
        [class.animate-bounce]="hasNewNotifications()"
      >
        {{ unreadCount() }}
      </span>
    </span>
  }
  <mat-icon
    [svgIcon]="'heroicons_outline:bell'"
    [class.text-primary]="hasNewNotifications()"
  ></mat-icon>
</button>

<!-- Notifications panel -->
<ng-template #notificationsPanel>
  <div
    class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl"
  >
    <!-- Header -->
    <div
      class="flex shrink-0 items-center bg-primary py-4 pl-6 pr-4 text-on-primary"
    >
      <div class="-ml-1 mr-3 sm:hidden">
        <button mat-icon-button (click)="closePanel()">
          <mat-icon
            class="text-current icon-size-5"
            [svgIcon]="'heroicons_solid:x-mark'"
          ></mat-icon>
        </button>
      </div>
      <div class="text-lg font-medium leading-10">Notifications</div>
      <div class="ml-auto flex items-center space-x-2">
        <button
          class="dark:text-white"
          mat-icon-button
          [disabled]="unreadCount() === 0"
          [matTooltip]="'Mark all as read'"
          (click)="markAllAsRead()"
        >
          <mat-icon
            class="text-current icon-size-5"
            [svgIcon]="'heroicons_solid:envelope-open'"
          ></mat-icon>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-card border-b px-4 pt-4 filters-section">
      <!-- Category Filter and Read Toggle in same row -->
      <div class="flex items-start space-x-4">
        <!-- Category Filter -->
        <div class="flex-1">
          <mat-form-field appearance="outline" class="w-full text-sm">
            <mat-label>Filter by Category</mat-label>
            <mat-select
              [value]="selectedCategoryId()"
              (selectionChange)="onCategoryChange($event.value)"
            >
              <mat-option [value]="0">All Categories</mat-option>
              @for (category of categories(); track category.id) {
                <mat-option [value]="category.id">
                  {{ formatCategoryCode(category.code) }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Read Toggle with dynamic title at top -->
        <div class="flex flex-col items-end">
          <span class="text-xs text-gray-600 dark:text-gray-400 mb-1">
            {{ showReadNotifications() ? 'Read' : 'Unread' }}
          </span>
          <mat-slide-toggle
            [checked]="showReadNotifications()"
            (change)="onToggleReadNotifications($event.checked)"
            color="primary"
          >
          </mat-slide-toggle>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div
      class="bg-card relative flex flex-auto flex-col divide-y overflow-y-auto sm:max-h-120"
    >
      <!-- Loading indicator -->
      @if (isLoading() && notifications().length === 0) {
        <div
          class="flex flex-auto flex-col items-center justify-center px-8 py-12"
        >
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
          ></div>
          <div class="mt-4 text-sm text-secondary">
            Loading notifications...
          </div>
        </div>
      }

      <!-- Notifications -->
      @for (
        notification of notifications();
        track trackByFn($index, notification)
      ) {
        <div
          class="group flex hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5 transition-opacity"
          [ngClass]="{
            'bg-blue-50 dark:bg-blue-900/10 opacity-100': !notification.has_read,
            'opacity-75': notification.has_read
          }"
        >
          <!-- Notification content with integrated mark-as-read button -->
          <div
            class="flex flex-auto py-5 pl-4 transition-all"
            [ngClass]="{
              'cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/20': isNotificationClickable(notification),
              'cursor-default opacity-60': notification.has_read && notification.link,
              'cursor-default': !notification.link
            }"
            (click)="onNotificationContentClick(notification)"
            [title]="isNotificationClickable(notification) ? 'Click to open notification and mark as read' : (notification.has_read && notification.link ? 'Notification already read' : 'No link available')"
          >
            <ng-container
              *ngTemplateOutlet="notificationContent"
            ></ng-container>
          </div>

        </div>

        <!-- Message content template -->
        <ng-template #notificationContent>
          <!-- Dynamic icon based on category and link -->
          @if (!notification.image) {
            <div
              class="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
              [ngClass]="{
                'bg-gray-100 dark:bg-gray-700': notification.has_read,
                'bg-primary-100 dark:bg-primary-700': !notification.has_read
              }"
            >
              <mat-icon
                class="icon-size-5"
                [ngClass]="{
                  'text-gray-600 dark:text-gray-400': notification.has_read,
                  'text-primary-600 dark:text-primary-200': !notification.has_read
                }"
              >
                {{ getNotificationIcon(notification) }}
              </mat-icon>
            </div>
          }
          <!-- Image -->
          @if (notification.image) {
            <img
              class="mr-4 h-8 w-8 shrink-0 overflow-hidden rounded-full object-cover object-center"
              [src]="notification.image"
              [alt]="'Notification image'"
            />
          }
          <!-- Subtitle as main title and description as content -->
          <div class="flex flex-auto flex-col">
            <!-- Title with category badge -->
            @if (notification.subtitle) {
              <div class="flex items-center gap-2 flex-wrap">
                <div
                  #subtitleElement
                  class="line-clamp-2 flex-1 transition-all"
                  [ngClass]="{
                    'font-bold': !notification.has_read,
                    'font-semibold': notification.has_read
                  }"
                  [innerHTML]="notification.subtitle"
                  [matTooltip]="getPlainText(notification.subtitle)"
                  [matTooltipDisabled]="!isTextTruncated(subtitleElement)"
                  matTooltipPosition="above"
                  matTooltipClass="notification-tooltip"
                  matTooltipShowDelay="300"
                ></div>
                <!-- Read/Unread indicator -->
            <div class="flex flex-col">
              <button
                mat-icon-button
                (click)="toggleRead(notification); $event.stopPropagation()"
                (mouseenter)="$event.stopPropagation()"
                (mouseleave)="$event.stopPropagation()"
                [matTooltip]="
                  notification.has_read ? 'Mark as unread' : 'Mark as read'
                "
              >
                <span
                  class="h-2 w-2 rounded-full"
                  [ngClass]="{
                    'bg-gray-400 dark:bg-gray-500 sm:opacity-0 sm:group-hover:opacity-100':
                      notification.has_read,
                    'bg-primary': !notification.has_read,
                  }"
                ></span>
              </button>
            </div>
              </div>
            }
            @if (notification.description) {
              <div
                #descriptionElement
                class="line-clamp-2 text-sm mt-1 pr-4"
                [innerHTML]="notification.description"
                [matTooltip]="getPlainText(notification.description)"
                [matTooltipDisabled]="!isTextTruncated(descriptionElement)"
                matTooltipPosition="above"
                matTooltipClass="notification-tooltip"
                matTooltipShowDelay="300"
              ></div>
            }
            <!-- Time and category badge -->
            <div class="flex items-center gap-2 mt-2">
              <span class="text-secondary text-sm leading-none">
                {{ formatTime(notification.created_at) }}
              </span>
              @if (notification.notification_category?.code) {
                <div class="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full text-gray-600 dark:text-gray-300 category-badge text-center shrink-0">
                  {{ formatCategoryCode(notification.notification_category.code) }}
                </div>
              }
            </div>

            <!-- Action buttons on separate line, centered -->
            @if (getActions(notification).length > 0) {
              <div class="flex flex-col gap-2 mt-3">
                <div class="flex items-center justify-end gap-2">
                  @for (action of getActions(notification); track action.id) {
                    @if (!hasActionFailed(notification, action.id)) {
                      <!-- Normal action button -->
                      <button
                        class="text-xs px-3 py-1.5 rounded-full transition-colors border-0 flex items-center gap-1.5 font-medium"
                        [ngClass]="{
                          'bg-primary cursor-pointer hover:opacity-80 text-white': action.color === 'primary',
                          'bg-red-500 cursor-pointer hover:opacity-80 text-white': action.color === 'warn',
                          'bg-blue-500 cursor-pointer hover:opacity-80 text-white': action.color === 'accent'
                        }"
                        (click)="executeAction(action.id, notification); $event.stopPropagation()"
                        [disabled]="isExecutingAction()"
                        [matTooltip]="action.tooltip"
                      >
                        @if (isExecutingAction() && currentActionId() === action.id) {
                          <span class="text-xs text-white">Processing...</span>
                        } @else {
                          <span class="text-xs text-white">
                            {{ getActionLabel(action.id) }}
                          </span>
                        }
                      </button>
                    } @else {
                      <!-- Error state -->
                      <div class="flex flex-col items-end gap-2">
                        <!-- Error message -->
                        <div class="text-xs text-red-600 dark:text-red-400 max-w-48 text-right leading-tight">
                          {{ getActionError(notification, action.id)?.message }}
                        </div>
                        
                        @if (getActionError(notification, action.id)?.isOneTimeAction) {
                          <!-- For one-time actions, ask if user wants to mark as completed -->
                          <div class="flex gap-2">
                            <button
                              class="text-xs px-2 py-1 rounded-full transition-colors border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                              (click)="clearActionError(notification.oid, action.id); $event.stopPropagation()"
                              [matTooltip]="'Dismiss error'"
                            >
                              Dismiss
                            </button>
                            <button
                              class="text-xs px-2 py-1 rounded-full transition-colors bg-orange-500 hover:bg-orange-600 text-white"
                              (click)="markActionAsCompleted(action.id, notification); $event.stopPropagation()"
                              [matTooltip]="'Mark this action as completed to remove it'"
                            >
                              Mark as Done
                            </button>
                          </div>
                        } @else {
                          <!-- For non-one-time actions, just show dismiss -->
                          <button
                            class="text-xs px-2 py-1 rounded-full transition-colors border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                            (click)="clearActionError(notification.oid, action.id); $event.stopPropagation()"
                            [matTooltip]="'Dismiss error'"
                          >
                            Dismiss
                          </button>
                        }
                      </div>
                    }
                  }
                </div>
              </div>
            }
          </div>
        </ng-template>
      }

      <!-- Load more button -->
      @if (notifications().length > 0 && !isLoading() && hasMore()) {
        <div class="p-4 text-center border-t">
          <button mat-button (click)="loadMore()" [disabled]="isLoading()">
            Load More
          </button>
        </div>
      }

      <!-- Loading more indicator -->
      @if (isLoading() && notifications().length > 0) {
        <div class="p-4 text-center border-t">
          <div
            class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mx-auto"
          ></div>
          <div class="mt-2 text-xs text-secondary">Loading more...</div>
        </div>
      }

      <!-- No notifications -->
      @if (!notifications() || notifications().length === 0) {
        @if (!isLoading()) {
          <div
            class="flex flex-auto flex-col items-center justify-center px-8 py-12 sm:justify-start"
          >
            <div
              class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600"
            >
              <mat-icon
                class="text-primary-700 dark:text-primary-50"
                [svgIcon]="'heroicons_outline:inbox'"
              ></mat-icon>
            </div>
            @if (selectedCategoryId() !== 0 || showReadNotifications()) {
              <div class="mt-5 text-2xl font-semibold tracking-tight">
                No matching notifications
              </div>
              <div
                class="text-secondary mt-1 w-full max-w-60 text-center text-md"
              >
                Try adjusting your filters to see more notifications.
              </div>
            } @else {
              <div class="mt-5 text-2xl font-semibold tracking-tight">
                No unread notifications
              </div>
              <div
                class="text-secondary mt-1 w-full max-w-60 text-center text-md"
              >
                All caught up! New notifications will appear here.
              </div>
            }
          </div>
        }
      }
    </div>
  </div>
</ng-template>
